import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/Badge";
import { Card } from "@/components/ui/Card";
import { PriceTag } from "@/components/ui/PriceTag";
import { ApiError, getWishlist, removeWishlistItem, type WishlistItem } from "@/lib/api-client";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";

const WISHLIST_QUERY_KEY = ["wishlist"] as const;

/**
 * Page favoris/wishlist (§10.2 "Mes favoris") — port de
 * apps/web/app/(account)/compte/favoris/page.tsx +
 * apps/web/app/(account)/compte/favoris/actions.ts (`removeWishlistItemAction`
 * devient une `useMutation` au lieu d'une Server Action liée à un
 * `<form action=...>`).
 *
 * `WishlistItem` (cf. `@/lib/api-client`) est projeté depuis une VARIANTE
 * précise, pas un produit (`WishlistItem.variantId` en base) : un prix
 * unique, une image unique — pas la forme `ProductListItem` (fourchette de
 * prix, plusieurs médias). On réutilise donc la même structure visuelle
 * (image carrée, nom, prix) que l'original plutôt qu'un composant `ProductCard`
 * partagé (propriété d'un autre atelier en parallèle, cf. contraintes de ce
 * lot — hors périmètre de toute façon, `ProductCard` attend la forme
 * `ProductListItem`).
 */
export function CompteFavorisPage() {
  const { logout } = useCustomerAuth();
  const queryClient = useQueryClient();

  const { data, error, isPending } = useQuery({
    queryKey: WISHLIST_QUERY_KEY,
    queryFn: () => getWishlist(),
  });
  const wishlist: WishlistItem[] = data ?? [];

  const removeMutation = useMutation({
    // Retrait idempotent côté API : soumettre deux fois le même retrait n'est pas une erreur.
    mutationFn: (variantId: string) => removeWishlistItem(variantId),
    onSuccess: (updatedWishlist) => {
      // L'API renvoie déjà la liste à jour : on l'écrit directement dans le
      // cache plutôt que de redéclencher un round-trip réseau superflu.
      queryClient.setQueryData(WISHLIST_QUERY_KEY, updatedWishlist);
    },
  });

  if (error instanceof ApiError && error.status === 401) {
    logout();
    return <Navigate to="/connexion" replace />;
  }

  if (isPending) {
    return (
      <div className="container-vvs py-8 lg:py-12">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Mes favoris</h1>
        <p className="mt-3 font-body text-sm text-muted">Chargement de vos favoris…</p>
      </div>
    );
  }

  if (error) {
    // Panne réseau/API : message honnête plutôt qu'une page qui plante (§11.1).
    return (
      <div className="container-vvs py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Mes favoris</h1>
        <p className="mt-3 font-body text-sm text-muted">
          Vos favoris sont momentanément indisponibles. Réessayez dans un instant.
        </p>
      </div>
    );
  }

  return (
    <div className="container-vvs py-8 lg:py-12">
      <h1 className="font-display text-2xl font-bold text-navy sm:text-3xl">Mes favoris</h1>
      <p className="mt-2 font-body text-sm text-muted">
        {wishlist.length > 0
          ? `${wishlist.length} produit${wishlist.length > 1 ? "s" : ""} enregistré${wishlist.length > 1 ? "s" : ""}.`
          : "Vous n'avez encore ajouté aucun produit à vos favoris."}
      </p>

      {wishlist.length === 0 ? (
        <div className="mt-8 rounded-sm border border-line bg-white p-8 text-center">
          <p className="font-body text-base text-ink">Votre liste de favoris est vide.</p>
          <p className="mt-2 font-body text-sm text-muted">
            Parcourez le catalogue pour trouver des produits à ajouter à vos favoris.
          </p>
          <Link
            to="/catalogue"
            className="mt-4 inline-block rounded-sm bg-navy px-6 py-2.5 font-body text-sm font-medium text-white hover:bg-navy/90"
          >
            Parcourir le catalogue
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5">
          {wishlist.map((item) => (
            <Card key={item.variantId} className="group flex h-full flex-col overflow-hidden">
              <Link to={`/p/${item.productSlug}`} className="flex flex-1 flex-col">
                <div className="relative aspect-square w-full overflow-hidden bg-cream2">
                  {!item.inStock ? (
                    <div className="absolute left-2 top-2 z-10">
                      <Badge tone="outOfStock">Rupture</Badge>
                    </div>
                  ) : null}

                  {item.image ? (
                    <img
                      src={item.image.url}
                      alt={item.image.alt ?? item.productName}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-4 text-center">
                      <span aria-hidden="true" className="text-3xl text-line">
                        {"\u{1F4E6}"}
                      </span>
                      <span className="line-clamp-2 font-body text-xs text-muted">{item.productName}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-1 flex-col gap-1 p-3">
                  <h3 className="line-clamp-2 font-body text-sm font-medium text-ink">
                    {item.productName}
                    {item.variantNameSuffix ? ` — ${item.variantNameSuffix}` : ""}
                  </h3>

                  <div className="mt-auto pt-1">
                    <PriceTag
                      amountMinor={item.price.amountMinor}
                      compareAtAmountMinor={item.compareAtPrice?.amountMinor ?? null}
                    />
                  </div>
                </div>
              </Link>

              <button
                type="button"
                onClick={() => removeMutation.mutate(item.variantId)}
                disabled={removeMutation.isPending && removeMutation.variables === item.variantId}
                className="w-full border-t border-line px-3 py-2 text-center font-body text-xs font-medium text-navy hover:bg-cream2 disabled:opacity-50"
              >
                {removeMutation.isPending && removeMutation.variables === item.variantId
                  ? "Retrait…"
                  : "Retirer"}
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
